import { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitch } from './LanguageSwitch';
import { Logo } from './Logo';
import { useAuth } from '../context/AuthContext';
import { scrollToSection } from '@/components/LazySection';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [hovered, setHovered] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const lastScrollY = useRef(0);
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (href: string) => {
    setIsMenuOpen(false);

    if (href.startsWith('/#')) {
      const elementId = href.substring(2);
      // Sections below the hero mount lazily: wait for the target instead of a fixed delay
      if (location.pathname !== '/') navigate('/');
      scrollToSection(elementId);
    }
  };

  useEffect(() => {
    let rafId: number | null = null;

    const getScrollY = () => window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        const currentScrollY = getScrollY();
        setScrolled(currentScrollY > 8);
        if (currentScrollY > lastScrollY.current && currentScrollY > 120) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
        lastScrollY.current = currentScrollY;
        rafId = null;
      });
    };

    // The page may scroll on body instead of window: listen in the capture phase to catch both
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true } as EventListenerOptions);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const menuItems = [
    { name: t('nav.features'), href: '/#functions' },
    { name: t('nav.pricing'), href: '/pricing' },
    { name: t('nav.blog'), href: '/blog' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  const linkClass = 'relative z-10 rounded-full px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors';
  const elevated = scrolled || isMenuOpen;

  return (
    <>
      <motion.nav
        initial={false}
        animate={{ y: isVisible || isMenuOpen ? 0 : -96 }}
        transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
        className="fixed inset-x-0 top-0 z-50 px-3 pt-3 md:px-6"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div
          className={`mx-auto flex h-14 max-w-6xl items-center gap-6 rounded-full pl-5 pr-2 transition-[background-color,box-shadow,backdrop-filter] duration-300 ease-out ${
            elevated || isMobile ? 'glass' : 'bg-transparent shadow-none'
          }`}
        >
          {/* Logo */}
          <Link
            to="/"
            aria-label="TrenioApp"
            className="shrink-0"
            onClick={(e) => {
              if (location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
                document.body.scrollTo({ top: 0, behavior: 'smooth' });
                const root = document.getElementById('root');
                if (root) root.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <Logo />
          </Link>

          {/* Desktop Menu: a soft pill follows the pointer between links */}
          <div className="hidden xl:flex items-center" onMouseLeave={() => setHovered(null)}>
            {menuItems.map((item) => {
              const pill = hovered === item.name && (
                <motion.span
                  layoutId="nav-hover"
                  className="absolute inset-0 -z-10 rounded-full bg-foreground/[0.06]"
                  transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
                />
              );
              return item.href.startsWith('/#') ? (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href)}
                  onMouseEnter={() => setHovered(item.name)}
                  className={`${linkClass} bg-transparent border-none cursor-pointer`}
                >
                  {pill}
                  {item.name}
                </button>
              ) : (
                <Link key={item.name} to={item.href} onMouseEnter={() => setHovered(item.name)} className={linkClass}>
                  {pill}
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Actions */}
          <div className="hidden xl:flex items-center gap-2 shrink-0 ml-auto">
            <LanguageSwitch />
            {user ? (
              <Link
                to="/dashboard"
                className="press inline-flex h-10 items-center rounded-full bg-foreground px-5 text-sm font-medium text-background hover:bg-foreground/90"
              >
                {t('nav.goToPanel')}
              </Link>
            ) : (
              <>
                <Link to="/login" state={{ signup: false }} className="rounded-full px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
                  {t('common.login')}
                </Link>
                <Link
                  to="/login"
                  state={{ signup: true }}
                  className="press inline-flex h-10 items-center rounded-full bg-foreground px-5 text-sm font-medium text-background hover:bg-foreground/90"
                >
                  {t('nav.getStarted')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? t('common.closeMenu') : t('common.openMenu')}
            aria-expanded={isMenuOpen}
            className="press xl:hidden ml-auto flex h-10 w-10 items-center justify-center rounded-full text-foreground hover:bg-foreground/[0.06]"
          >
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={isMenuOpen ? 'x' : 'menu'}
                initial={{ opacity: 0, rotate: -45, scale: 0.8 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 45, scale: 0.8 }}
                transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                className="flex"
              >
                {isMenuOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu: a glass sheet that grows out of the capsule */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            className="fixed inset-0 z-40 xl:hidden h-[100dvh]"
          >
            <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={() => setIsMenuOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98, transition: { duration: 0.15 } }}
              transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
              className="absolute inset-x-3 rounded-3xl glass p-3 text-foreground md:inset-x-6"
              style={{ top: 'calc(env(safe-area-inset-top, 0px) + 76px)', transformOrigin: 'top center' }}
            >
              <div className="flex flex-col gap-0.5">
                {menuItems.map((item) => (
                  item.href.startsWith('/#') ? (
                    <button
                      key={item.name}
                      onClick={() => handleNavClick(item.href)}
                      className="flex h-12 items-center rounded-2xl px-4 text-left text-base text-foreground hover:bg-muted transition-colors bg-transparent border-none cursor-pointer"
                    >
                      {item.name}
                    </button>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="flex h-12 items-center rounded-2xl px-4 text-base text-foreground hover:bg-muted transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )
                ))}
              </div>

              <div className="mt-3 flex flex-col gap-2 border-t border-border px-1 pt-4">
                <div className="mb-2">
                  <LanguageSwitch placement="top" align="start" className="w-fit" />
                </div>
                {user ? (
                  <Link
                    to="/dashboard"
                    className="flex h-11 items-center justify-center rounded-full bg-foreground text-sm font-medium text-background press"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('nav.goToPanel')}
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      state={{ signup: true }}
                      className="flex h-11 items-center justify-center rounded-full bg-foreground text-sm font-medium text-background press"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('nav.getStarted')}
                    </Link>
                    <Link
                      to="/login"
                      state={{ signup: false }}
                      className="flex h-11 items-center justify-center rounded-full border border-border text-sm text-foreground hover:bg-muted press"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('common.login')}
                    </Link>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
